import { useState, useEffect } from 'react';
import { Menu, X, Phone, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import featureFlags from '../config/featureFlags';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_67b9bb81-7260-44ec-aaa3-ea9719c1ace2/artifacts/fzzdas0u_100915ed-2232-44be-a466-4b016240034b.jpeg";

// Navigation items - Shop item is conditionally included based on feature flag
const getNavItems = () => {
  const items = [
    { label: 'Start', href: '#start' },
    { label: 'Leistungen', href: '#leistungen' },
    { label: 'Referenzen', href: '#referenzen' },
  ];
  
  // Only add Sticker-Shop if enabled
  if (featureFlags.SHOP_ENABLED) {
    items.push({ label: 'Sticker', href: '#sticker' });
  }
  
  items.push(
    { label: 'Über uns', href: '#ueber-uns' },
    { label: 'Kontakt', href: '#kontakt' }
  );
  
  return items;
};

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navItems = getNavItems();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href) => {
    setIsMobileMenuOpen(false);
    const element = document.querySelector(href);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <header
      data-testid="navigation-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'glass-header shadow-sm' 
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#start"
            onClick={(e) => { e.preventDefault(); scrollToSection('#start'); }}
            className="flex items-center"
            data-testid="nav-logo"
          >
            <img
              src={LOGO_URL}
              alt="AKW Design Logo"
              className="h-12 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => scrollToSection(item.href)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-teal-600 transition-colors rounded-lg hover:bg-teal-50"
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </button>
            ))}
            {/* External Link: Sticker kaufen */}
            <a
              href="https://www.folicut.de"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-teal-600 transition-colors rounded-lg hover:bg-teal-50 flex items-center gap-1.5"
              data-testid="nav-sticker-kaufen"
            >
              Sticker kaufen
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-3">
            <a href="tel:+4915510904109" className="flex items-center gap-2 text-sm text-slate-600 hover:text-teal-600 transition-colors">
              <Phone className="w-4 h-4" />
              <span>+49 155 10904109</span>
            </a>
            <Button
              onClick={() => scrollToSection('#kontakt')}
              className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6 shadow-lg hover:shadow-teal-500/20 transition-all"
              data-testid="nav-cta-button"
            >
              Angebot anfragen
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-slate-700" />
            ) : (
              <Menu className="w-6 h-6 text-slate-700" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-slate-100 shadow-lg animate-fadeInUp"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.href}
                  onClick={() => scrollToSection(item.href)}
                  className="block w-full text-left px-4 py-3 text-slate-700 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </button>
              ))}
              {/* External Link: Sticker kaufen (Mobile) */}
              <a
                href="https://www.folicut.de"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 w-full text-left px-4 py-3 text-slate-700 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                data-testid="mobile-nav-sticker-kaufen"
              >
                Sticker kaufen
                <ExternalLink className="w-4 h-4" />
              </a>
              <div className="pt-4 border-t border-slate-100">
                <Button
                  onClick={() => scrollToSection('#kontakt')}
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full shadow-lg"
                  data-testid="mobile-cta-button"
                >
                  Angebot anfragen
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
